/** Detalhe do cliente + geração de link mágico de aprovação. Onda 13. */
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import type { ClientDTO } from "@unik/shared";
import { api } from "../../lib/api";
import { AdminShell } from "../../ui/AdminShell";
import { Button } from "../../ui/Button";
import { Card } from "../../ui/Card";
import { TIER_LABEL, STATUS_LABEL } from "../../lib/labels";
import { fmtInt, fmtGrowthPerMonth } from "../../lib/metrics";

interface MagicLink {
  id: string;
  label: string | null;
  expiresAt: string;
  lastUsedAt: string | null;
  useCount: number;
  createdAt: string;
}
interface Generated {
  url: string;
  message: string;
  whatsappUrl: string;
}
interface ArchGenerated {
  url: string;
  message: string;
}

export function ClienteDetalhe() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState<ClientDTO | null>(null);
  const [links, setLinks] = useState<MagicLink[]>([]);
  const [generated, setGenerated] = useState<Generated | null>(null);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [desligando, setDesligando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [arch, setArch] = useState<ArchGenerated | null>(null);
  const [archCopied, setArchCopied] = useState(false);
  const [archBusy, setArchBusy] = useState(false);

  async function load() {
    try {
      const [c, l] = await Promise.all([
        api.get<ClientDTO>(`/clients/${id}`),
        api.get<MagicLink[]>(`/clients/${id}/magic-links`),
      ]);
      setClient(c);
      setLinks(l);
    } catch {
      setError("Cliente não encontrado.");
    }
  }
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function gerarLink() {
    setBusy(true);
    try {
      const res = await api.post<Generated>(`/clients/${id}/magic-links`, {});
      setGenerated(res);
      setCopied(false);
      await load();
    } finally {
      setBusy(false);
    }
  }
  async function revogar(linkId: string) {
    await api.post(`/clients/${id}/magic-links/${linkId}/revoke`).catch(() => undefined);
    await load();
  }
  async function copiar() {
    if (generated === null) return;
    await navigator.clipboard.writeText(generated.message).catch(() => undefined);
    setCopied(true);
  }
  async function gerarArquitetura() {
    setArchBusy(true);
    try {
      const res = await api.post<ArchGenerated>(`/clients/${id}/architecture/share`, {});
      setArch(res);
      setArchCopied(false);
    } finally {
      setArchBusy(false);
    }
  }
  async function copiarArquitetura() {
    if (arch === null) return;
    await navigator.clipboard.writeText(arch.message).catch(() => undefined);
    setArchCopied(true);
  }
  async function revogarArquitetura() {
    await api.post(`/clients/${id}/architecture/revoke`).catch(() => undefined);
    setArch(null);
  }

  async function desligar() {
    if (client === null) return;
    if (!window.confirm(`Desligar ${client.name}? Ele vai para o histórico de desativados.`)) return;
    setDesligando(true);
    try {
      await api.patch(`/clients/${id}/status`, { status: "archived" });
      navigate("/gestao");
    } finally {
      setDesligando(false);
    }
  }

  if (error !== null)
    return <AdminShell><p className="type-body text-danger">{error}</p></AdminShell>;
  if (client === null)
    return <AdminShell><p className="type-body text-text-secondary">Carregando…</p></AdminShell>;

  return (
    <AdminShell>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="type-h1 text-text-cream">{client.name}</h1>
          {client.businessName !== null && (
            <p className="type-body text-text-secondary">{client.businessName}</p>
          )}
        </div>
        <div className="flex gap-2">
          <Link to={`/clientes/${id}/editar`}>
            <Button variant="secondary" size="sm">Editar</Button>
          </Link>
          {client.status !== "archived" && (
            <Button variant="danger" size="sm" onClick={desligar} loading={desligando}>
              Desligar cliente
            </Button>
          )}
        </div>
      </div>

      <div className="mt-3 flex flex-wrap gap-3 type-caption text-text-tertiary">
        <span className="text-gold">{TIER_LABEL[client.tier]}</span>
        <span>{STATUS_LABEL[client.status]}</span>
        {client.city !== null && <span>{client.city}</span>}
        {client.segment !== null && <span>{client.segment}</span>}
      </div>

      <div className="mt-4 flex gap-4 type-caption text-text-tertiary">
        <span>{client.stats.pendingApproval} aguardando</span>
        <span>{client.stats.approved} aprovados</span>
        <span>{client.stats.published} publicados</span>
      </div>

      <div className="mt-6 flex flex-wrap gap-3">
        <Link to={`/clientes/${id}/briefing`}>
          <Button variant="secondary">Briefing</Button>
        </Link>
        <Link to={`/clientes/${id}/timeline`}>
          <Button variant="secondary">Linha do tempo</Button>
        </Link>
        <Link to={`/clientes/${id}/esteira`}>
          <Button variant="secondary">Ver esteira</Button>
        </Link>
        <Link to={`/clientes/${id}/kanban`}>
          <Button variant="secondary">Fluxo (Kanban)</Button>
        </Link>
        <Link to={`/clientes/${id}/dashboard`}>
          <Button variant="secondary">Dashboard</Button>
        </Link>
        <Link to={`/clientes/${id}/mapas`}>
          <Button variant="secondary">Mapas Unik</Button>
        </Link>
      </div>

      <Card className="mt-8">
        <div className="flex items-center justify-between gap-3">
          <h2 className="type-h3 text-text-cream">Crescimento</h2>
          <Link to={`/clientes/${id}/dashboard`} className="type-caption text-gold">
            Ver dashboard completo →
          </Link>
        </div>
        {client.growth.followersCurrent === null && client.growth.storyViewsCurrent === null ? (
          <p className="type-body-sm mt-2 text-text-secondary">
            Nenhum número registrado ainda. Registre em Dashboard → "Registrar números".
          </p>
        ) : (
          <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
            <div>
              <p className="type-caption text-text-tertiary">Seguidores iniciais</p>
              <p className="type-h3 mt-0.5 text-text-cream">
                {client.growth.followersBaseline === null ? "—" : fmtInt(client.growth.followersBaseline)}
              </p>
            </div>
            <div>
              <p className="type-caption text-text-tertiary">Crescimento médio</p>
              <p className="type-h3 mt-0.5 text-text-cream">{fmtGrowthPerMonth(client.growth.followersGrowthPerMonth)}</p>
            </div>
            <div>
              <p className="type-caption text-text-tertiary">Seguidores agora</p>
              <p className="type-h3 mt-0.5 text-text-cream">
                {client.growth.followersCurrent === null ? "—" : fmtInt(client.growth.followersCurrent)}
              </p>
            </div>
            <div>
              <p className="type-caption text-text-tertiary">Stories · última medição</p>
              <p className="type-h3 mt-0.5 text-text-cream">
                {client.growth.storyViewsCurrent === null ? "—" : fmtInt(client.growth.storyViewsCurrent)}
              </p>
            </div>
            <div>
              <p className="type-caption text-text-tertiary">Stories · média</p>
              <p className="type-h3 mt-0.5 text-text-cream">
                {client.growth.storyViewsAverage === null ? "—" : fmtInt(client.growth.storyViewsAverage)}
              </p>
            </div>
            <div>
              <p className="type-caption text-text-tertiary">Stories · próx. dias</p>
              <p className="type-h3 mt-0.5 text-text-cream">
                {client.growth.storyViewsNextDays === null ? "—" : fmtInt(client.growth.storyViewsNextDays)}
              </p>
            </div>
          </div>
        )}
      </Card>

      <Card className="mt-4">
        <h2 className="type-h3 text-text-cream">Arquitetura de Onboarding</h2>
        <p className="type-body-sm mt-2 text-text-secondary">
          O roteiro completo — carta, jeito Unik, cronograma, regras — num link pessoal e só de
          leitura. Ele mostra "você está aqui" de acordo com o progresso real do cliente.
        </p>
        <div className="mt-4">
          <Button onClick={gerarArquitetura} loading={archBusy}>Gerar link</Button>
        </div>
        {arch !== null && (
          <div className="mt-4 rounded-[var(--radius-control)] border border-border-subtle p-4">
            <p className="type-caption break-all text-text-secondary">{arch.url}</p>
            <div className="mt-3 flex flex-wrap gap-3">
              <Button size="sm" onClick={copiarArquitetura}>{archCopied ? "Copiado!" : "Copiar mensagem"}</Button>
              <Button size="sm" variant="ghost" onClick={revogarArquitetura}>Desativar link</Button>
            </div>
          </div>
        )}
      </Card>

      <Card className="mt-4">
        <h2 className="type-h3 text-text-cream">Link de aprovação</h2>
        <p className="type-body-sm mt-2 text-text-secondary">
          Gere um link para a cliente aprovar os conteúdos. Ela abre no celular, sem senha.
        </p>
        <div className="mt-4">
          <Button onClick={gerarLink} loading={busy}>Gerar link</Button>
        </div>

        {generated !== null && (
          <div className="mt-4 rounded-[var(--radius-control)] border border-border-subtle p-4">
            <p className="type-caption break-all text-text-secondary">{generated.url}</p>
            <div className="mt-3 flex flex-wrap gap-3">
              <Button size="sm" onClick={copiar}>{copied ? "Copiado!" : "Copiar mensagem"}</Button>
              <a href={generated.whatsappUrl} target="_blank" rel="noreferrer">
                <Button size="sm" variant="secondary">Abrir no WhatsApp</Button>
              </a>
            </div>
          </div>
        )}

        {links.length > 0 && (
          <div className="mt-6 flex flex-col gap-2">
            <p className="type-label text-text-secondary">Links ativos</p>
            {links.map((l) => (
              <div
                key={l.id}
                className="flex items-center justify-between gap-3 rounded-[var(--radius-control-sm)] border border-border-subtle px-3 py-2"
              >
                <span className="type-caption text-text-tertiary">
                  Usado {l.useCount}× · expira {new Date(l.expiresAt).toLocaleDateString("pt-BR")}
                </span>
                <Button size="sm" variant="ghost" onClick={() => revogar(l.id)}>Revogar</Button>
              </div>
            ))}
          </div>
        )}
      </Card>

      {client.positioning !== null && (
        <Card className="mt-4">
          <h2 className="type-h3 text-text-cream">Posicionamento</h2>
          <p className="type-body-sm mt-2 whitespace-pre-wrap text-text-secondary">
            {client.positioning}
          </p>
        </Card>
      )}
    </AdminShell>
  );
}
